import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import i18n from "@/libs/i18n"
import useCommonDashboards from '@/views/habit/useCommonDashboards'
import useCommonTodo from '@/views/apps/todo/useCommonTodo'
import useCommon from '@/views/organization/useCommon'
import { get, set, getMany, del } from 'idb-keyval'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'

export default function onlineUpdate() {
  const userData = store.state?.userStore?.userData
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { updateDailyDialogueWithKey } = useCommonDashboards()
  const { getEmailImprovementTemplate, addTask, formatEmailDate, getErrorTemplate, uploadFileToS3 } = useCommonTodo()
  const { formatDatePicker, updateConfirmationWithKey, createImprovements } = useCommon()
  const { getItem, getItems, updateItem, createItem, createItems, sendEmail } = useLambda()
  const { default_language, commitment_functionality } = JSON.parse(localStorage.getItem('clientData') || '{}')
  const postImprovement = (payload) => {
    return new Promise((resolve, reject) => {
      addTask(payload)
        .then(() => resolve())
        .catch(() => reject())
    })
  }

  const postDailyDialogue = async (payload) => {
    let dataFromPayload = {
      imgData: payload.imgData,
      improvementsNotMapped: payload.improvementsNotMapped,
      employeeSurveysToUpload: payload.employeeSurveysToUpload
    }
    delete payload.imgData
    delete payload.improvementsNotMapped
    delete payload.employeeSurveysToUpload
    
    try {
      payload.client_id = oid(payload.client_id)
      payload.created_by = oid(payload.created_by)
      payload.location = oid(payload.location)
      if (payload.physical_location) payload.physical_location = oid(payload.physical_location)
    
      // Create recognitions
      if (payload.recognitions?.length) {
        const recognitionsToCreate = payload.recognitions.map(e => ({
          ...e,
          client_id: oid(e.client_id),
          worker: oid(e.worker),
          motive: oid(e.motive),
          created_by: oid(e.created_by),
        }))

        const { insertedIds: newRecognitionIds } = await createItems({ collection: 'recognition', payload: recognitionsToCreate })
        payload.recognitions = Object.values(newRecognitionIds).map(id => oid(id))
      }

      // Create improvements
      if (payload.improvements?.length) {
        const improvementsToCreate = payload.improvements.map(e => ({
          ...e,
          client_id: oid(e.client_id),
          assignee: oid(e.assignee),
        }))

        const { insertedIds: newImprovementIds } = await createItems({ collection: 'improvement', payload: improvementsToCreate })
        payload.improvements = Object.values(newImprovementIds).map(id => oid(id))
      }

      // Create employee surveys
      if (dataFromPayload.employeeSurveysToUpload?.length) {
        const surveysToCreate = dataFromPayload.employeeSurveysToUpload.map(e => ({
          ...e,
          location: payload.location,
          physical_location: payload.physical_location || null,
          createdAt: date(new Date(e.createdAt)),
          read: true,
        }))

        const { insertedIds: newSurveyIds } = await createItems({ collection: 'employee_survey', payload: surveysToCreate })
        payload.employeeSurveys = Object.values(newSurveyIds).map(id => oid(id))
      }

      // Create daily dialogue
      const { insertedId } = await createItem({ collection: 'meeting', payload })
      if (!insertedId) throw new Error('Item not created')
      showSuccessMessage(i18n.t('message.daily_dialogue_created'))

      // Upload image to AWS and then update the daily dialogue in MongoDB with the AWS image key
      const dailyDialogueId = insertedId.toString()
      const { fileInfo, destinationFolder } = dataFromPayload.imgData || {}
      if (fileInfo) {
        uploadFileToS3(fileInfo, destinationFolder)
          .then((key) => updateDailyDialogueWithKey(dailyDialogueId, key))
          .catch((err) => console.log(err))
      }

      // Send email to assignees with the details of the improvements opportunity created
      dataFromPayload.improvementsNotMapped?.forEach(e => {
        if (e.assignee?.email) {
          const subject = i18n.t('message.improvement_opportunity_assigned')
          let bodyData = {
            name: e.assignee?.name,
            title: e.note,
            tags: e.domain ? i18n.t(`domain.${e.domain}`) : '',
            creator: userData.fullName,
            dueDate: formatEmailDate(e.dueDate),
          }
          if (e.metadata?.length) {
            e.metadata.forEach(e => {
              if (e.name === "creation_date") e.answer = formatEmailDate(e.answer)
              Object.assign(bodyData, {[e.name]: e.answer})
            })
          }
          const body = getEmailImprovementTemplate(bodyData)
          
          sendEmail({ to: [e.assignee.email], subject, htmlBody: body })
            .then((response) => {
              if (response.MessageId) showSuccessMessage(i18n.t('message.email_send_improvement_success'))
            })
            .catch((err) => {
              console.log(err)
              showErrorMessage(i18n.t('message.email_send_improvement_error'))
            })
        }
      })
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.daily_dialogue_error'))

      // Send email to admin with the error details
      const errorBodyData = {
        user: `${userData.fullName} (${userData.email})`,
        error: error,
        errorString: JSON.stringify(error),
        errorMessage: error.message,
        payload: JSON.stringify(payload),
        dataFromPayload: JSON.stringify(dataFromPayload),
        origin: 'OFFLINE daily dialogue upload'
      }

      const body = getErrorTemplate(errorBodyData)

      sendEmail({ to: ['mbalbi@addval.cl'], subject: 'Error cargando diálogo diario', htmlBody: body })
        .then((response) => {
          if (response.MessageId) showSuccessMessage('Email enviado al administrador con el error ocurrido')
        })
        .catch((err) => {
          console.log(err)
          showErrorMessage(i18n.t('message.email_send_improvement_error'))
        })
    }
  }
  
  const postEvent = async (payload, newImprovementIds) => {
    payload.client_id = oid(payload.client_id)
    payload.process = oid(payload.process)
    if (payload.attendee) payload.attendee = oid(payload.attendee)
    payload.organizer = oid(payload.organizer)
    payload.confirmation = payload.confirmation ? oid(payload.confirmation) : null
    payload.improvements = payload.improvements?.map(e => oid(e)) || []

    try {
      const { insertedId } = await createItem({ collection: 'event', payload })
      showSuccessMessage(i18n.t('message.confirmation_agenda'))

      // Update each improvement created with the origin_id from event
      if (insertedId) {
        newImprovementIds?.forEach(id => {
          try {
            const query = { _id: oid(id) }
            const action = { $set: { origin_id: oid(insertedId) } }
      
            updateItem({ collection: 'improvement', query, action })
          } catch (error) {
            console.log(error)
            showErrorMessage(commitment_functionality ? i18n.t('message.commitment_update_error') : i18n.t('message.improvement_update_error'))
          }
        })
      }
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.no_confirmation_agenda'))
    }
  }

  const fetchEventConfirmation = async (id) => {
    try {
      const query = { _id: oid(id) }
      const item = await getItem({ collection: 'event', query })
      if (!item) throw new Error('Item not found')
      return item.confirmation
    } catch (error) {
      console.log(error)
    }
  }

  const schedulleEvent = async (calendar, confirmationId, dataFromPayload) => {
    const startDateTime = formatDatePicker(dataFromPayload.eventDate)
    const endDateTime = formatDatePicker(dataFromPayload.eventDate, 13)

    if (confirmationId) {
      const firstDayOfCurrentMonth = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), 1)
      const firstDayOfNextMonth = new Date(startDateTime.getFullYear(), startDateTime.getMonth() + 1, 1)
      
      const query = {
        'extendedProps.calendar': { $ne: "Realizadas" },
        start: { $gte: date(firstDayOfCurrentMonth), $lt: date(firstDayOfNextMonth) },
        attendee: oid(dataFromPayload.worker),
        process: oid(dataFromPayload.processId)
      }

      try {
        const eventsToSearch = await getItems({ collection: 'event', query, options: { projection : { improvements: 1 }, sort: { start: 1 } } })
        
        if (eventsToSearch?.length) {
          const updateQuery = { _id: eventsToSearch[0]._id }
          const updatePayload = {
            extendedProps: { calendar: "Realizadas"},
            start: date(startDateTime),
            end: date(endDateTime),
            confirmation: oid(confirmationId)
          }

          // If there are new improvements, create improvements and merge them with old improvements from the event if there are any
          if (dataFromPayload.improvements?.length) {
            try {
              const newImprovementIds = await createImprovements(dataFromPayload.improvements, "event", eventsToSearch[0]._id.toString())
              const oldImprovementIds = eventsToSearch[0].improvements?.map(e => e.toString()) || []
              const consolidatedImprovements = [...oldImprovementIds, ...newImprovementIds]
              updatePayload.improvements = consolidatedImprovements.map(e => oid(e))
            } catch (error) {
              console.log(error)
            }
          }

          const action = { $set: updatePayload }

          await updateItem({ collection: 'event', query: updateQuery, action })
          return showSuccessMessage(i18n.t('message.Event_updated'))     
        }
      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('message.event_update_error'))
      }
    }

    const payload = {
      client_id: userData.client.$oid,
      process: dataFromPayload.processId,
      attendee: dataFromPayload.worker || null,
      organizer: dataFromPayload.supervisor,
      start: startDateTime,
      end: endDateTime,
      confirmation: confirmationId || null,
      extendedProps: { calendar: calendar || 'Pendientes' },
    }

    let newImprovementIds = []
    if (dataFromPayload.improvements?.length) {
      try {
        newImprovementIds = await createImprovements(dataFromPayload.improvements, "event", null)
        if (newImprovementIds?.length) {
          payload.improvements = newImprovementIds
        }
      } catch (error) {
        console.log(error)
      }
    }
  
    postEvent(payload, newImprovementIds)
  }

  const updateEventWithConfirmation = async (confirmationId, dataFromPayload) => {
    if (!confirmationId || !dataFromPayload.eventId) {
      schedulleEvent('Realizadas', confirmationId, dataFromPayload)
      return
    }

    // Fetch the event to see if its confirmation has already been done by another user
    try {
      const eventConfirmation = await fetchEventConfirmation(dataFromPayload.eventId)
      if (eventConfirmation) {
        schedulleEvent('Realizadas', confirmationId, dataFromPayload)
        return
      }
    } catch (error) {
      showErrorMessage(i18n.t('message.event_fetch_error'))
    }

    const payload = {
      confirmation: oid(confirmationId),
      process: oid(dataFromPayload.processId),
      extendedProps: { calendar: 'Realizadas' },
      attendee: oid(dataFromPayload.worker),
      start: date(formatDatePicker(dataFromPayload.eventDate)),
      end: date(formatDatePicker(dataFromPayload.eventDate, 13)),
    }

    const collection = 'event'
    const query = { _id: oid(dataFromPayload.eventId) }

    // If there are new improvements, create improvements and get event to see if it already has improvements to merge them
    if (dataFromPayload.improvements?.length) {
      try {
        const newImprovementIds = await createImprovements(dataFromPayload.improvements, "event", dataFromPayload.eventId)
  
        const eventResponse = await getItem({ collection, query })
        if (!eventResponse) throw new Error('Item not found')

        const oldImprovementIds = eventResponse.improvements?.map(e => e.toString()) || []
        const consolidatedImprovements = [...oldImprovementIds, ...newImprovementIds]

        payload.improvements = consolidatedImprovements.map(e => oid(e))
      } catch (error) {
        console.log(error)
      }
    }

    const action = { $set: payload }
  
    try {
      await updateItem({ collection, query, action })
      return showSuccessMessage(i18n.t('message.Event_updated'))     
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.event_update_error'))
    }
  }

  const postConfirmation = async (payload) => {
    const dataFromPayload = {
      processId: payload.process,
      worker: payload.worker,
      supervisor: payload.supervisor,
      eventDate: payload.date,
      imgData: payload.imgData,
      improvements: payload.improvements
    }

    delete payload.imgData
    delete payload.improvements

    if (payload.eventId) {
      dataFromPayload.eventId = payload.eventId
      delete payload.eventId
    }

    payload.client_id = oid(payload.client_id)
    payload.process = oid(payload.process)
    payload.worker = oid(payload.worker)
    payload.supervisor = oid(payload.supervisor)

    try {
      const { insertedId } = await createItem({ collection: 'confirmation', payload })
      if (!insertedId) throw new Error('Item not created')

      showSuccessMessage(i18n.t('message.confirmation_created'))
          
      const confirmationId = insertedId.toString()
      
      updateEventWithConfirmation(confirmationId, dataFromPayload)

      // Upload image to AWS and then update the confirmation in MongoDB with the AWS image key
      const { fileInfo, destinationFolder } = dataFromPayload.imgData || {}
      if (fileInfo) {
        uploadFileToS3(fileInfo, destinationFolder)
          .then((key) => updateConfirmationWithKey(confirmationId, key))
          .catch((err) => console.log(err))
      }
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.confirmation_error'))
    }
  }

  const updateLastSync = () => {
    const date = new Date()
    const actualDate = date.toLocaleDateString(`${default_language || 'en'}-US`)
    const actualTime = date.toLocaleTimeString(`${default_language || 'en'}-US`)
    localStorage.lastSync = `${actualDate} ${actualTime}`
  }

  const upload = async ({ category, key, storage }) => {
    let itemsStored = storage === "indexedDB" ? await get(key) : JSON.parse(localStorage[key])
    let itemsNotUploaded = []
    let uploadFunction
    switch (category) {
      case "dialogues":
        uploadFunction = (item) => postDailyDialogue(item)
        break;
      case "events":
        uploadFunction = (item) => postEvent(item)
        break;
      case "confirmations":
        uploadFunction = (item) => postConfirmation(item)
        break;
      case "improvements":
        uploadFunction = (item) => postImprovement(item)
        break;
      default:
        return;
    }
    for (let i = 0; i < itemsStored.length; i++) {
      const item = itemsStored[i]
      try {
        await uploadFunction(item)
        updateLastSync()
      } catch (error) {
        console.log(error)
        itemsNotUploaded.push(item)
      }
    }
    if (itemsNotUploaded.length) {
      if (storage === "indexedDB") {
        try {
          await set(key, itemsNotUploaded)
        } catch (error) {
          console.log(error)
        }
      } else {
        localStorage[key] = JSON.stringify(itemsNotUploaded)
      }
    } else {
      if (storage === "indexedDB") await del(key)
      else localStorage.removeItem(key)
    }
  }

  const uploadStorage = async (origin) => {
    const [dialogues, events, confirmations, improvements] = await getMany(['dialoguesToUpload', 'eventsToUpload', 'confirmationsToUpload', 'improvementsToUpload'])

    if (dialogues) await upload({ category: "dialogues", key: "dialoguesToUpload", storage: "indexedDB" })
    else if (localStorage.dialogues) await upload({ category: "dialogues", key: "dialogues", storage: "localStorage" })

    if (events) await upload({ category: "events", key: "eventsToUpload", storage: "indexedDB" })
    else if (localStorage.eventsForSchedule) await upload({ category: "events", key: "eventsForSchedule", storage: "localStorage" })

    if (confirmations) await upload({ category: "confirmations", key: "confirmationsToUpload", storage: "indexedDB" })
    else if (localStorage.confirmations) await upload({ category: "confirmations", key: "confirmations", storage: "localStorage" })

    if (improvements) await upload({ category: "improvements", key: "improvementsToUpload", storage: "indexedDB" })

    if (origin !== "forced") updateLastSync()
  }

  return {
    uploadStorage
  }
}
